/* assistant-drawer.js — the inline "Assistant" drawer for v2 workbench views
 * (Code view's Explain/Improve selection toolbar + whole-file Ask AI button;
 * Database view's "Ask about this table" affordance and Audit view's "Ask AI"
 * affordance on a journal entry reuse the same primitive). Wired via
 * workbench.js's `drawer: { render(ctx, bodyEl) }` config —
 * createWorkbench() un-hides `.wb-drawer` and calls
 * `renderAssistant(ctx, drawerBodyEl)`.
 *
 * renderAssistant(ctx, bodyEl):
 *   1. Renders a header (sparkles icon + ctx.label + "Continue in chat").
 *   2. kind "explain"/"improve" (ctx.prompt already built by the caller):
 *      AUTO-SENDS ctx.prompt on open, straight into the shared send+
 *      subscribe+stream flow (step 3/4 below).
 *      kind "table" (ctx.table = {name, isCustom, columns}, NO ctx.prompt) or
 *      kind "audit" (ctx.entry = the journal entry, including its
 *      already-loaded diff, NO ctx.prompt): renders three question CHIPS
 *      instead and does nothing else until one is clicked -- no auto-send.
 *      table-kind's three: "Explain this table", "Where is it used in
 *      scripts?", "Suggest example queries". audit-kind's three: "Why was
 *      this changed?", "Is it safe to undo?", "What depends on this?". A
 *      click builds the prompt via selection-prompt.js's
 *      buildTablePrompt(question, ctx.table) or buildAuditPrompt(question,
 *      ctx.entry) and feeds it into the SAME shared flow -- audit-kind's chip
 *      click is synchronous (the diff is already sitting on ctx.entry, no
 *      fetch at click time). "Continue in chat" starts disabled for
 *      chips-first kinds (nothing sent yet) and enables once a chip's send
 *      succeeds.
 *   3. The shared flow (`_runPrompt`, used by both paths above) sends the
 *      prompt into the ACTIVE conversation via sendComposerText(). If a run
 *      is already in flight, sendComposerText() returns false — the drawer
 *      shows "Agent is busy" and never subscribes.
 *   4. Subscribes assistantRunAtom and streams the active conversation's
 *      concatenated text parts into the answer container via renderMarkdown(),
 *      unsubscribing once the run finishes. Chips-first kinds (table, audit)
 *      can invoke this flow more than once per drawer instance (one per chip
 *      click); `_runPrompt`
 *      tears down its own prior subscription and resets the shared
 *      assistantRunAtom entry FIRST, so re-clicking a chip after an earlier
 *      one finished never flashes the earlier answer (store.js's
 *      `subscribe()` fires immediately with the current value) — same
 *      discipline as the top-level reopen teardown below, just scoped to one
 *      still-open drawer instance instead of the whole module.
 *
 * Improve completion: once an "improve"-kind run finishes (running===false),
 * `_renderImproveCard()` extracts the first fenced code block from the
 * accumulated answer (line-diff.js's extractFirstFencedBlock) and renders a
 * unified diff of ctx.selectionText vs that block into a dedicated
 * `.asst-improve-card` container (appended AFTER the streamed-markdown
 * answer, which keeps rendering unchanged for every ctx.kind, by design)
 * -- with Copy code / Apply in chat buttons. No fenced block found -> a
 * plain "No code block detected." note, no buttons.
 *
 * sendComposerText is loaded via a DYNAMIC import (like continueInChat's
 * showViewV2 below), not a static one — main.js is the app's boot entry and
 * pulls in a wide, DOM/`window`-touching module graph (e.g. model-picker.js
 * assigns to `window` at module top level). code-view.js imports this module
 * statically, and code-view.js is itself imported by plain-Node unit tests
 * (code-view.test.mjs) that exercise its pure row-mapping helpers only — a
 * static `import "./main.js"` here would drag that whole graph in and crash
 * those tests outside a browser. In the real app main.js is already loaded
 * (it's the entry chunk) by the time this drawer can ever open, so the
 * dynamic import resolves on the next microtask with the cached module --
 * no behavior change, just deferred module loading.
 *
 * GC — no leaked subscriptions / no unbounded assistantRunAtom growth:
 * workbench.js's closeDrawer() only clears drawerBodyEl's innerHTML; it does
 * not (and, per its own contract, cannot without a code change there) invoke
 * any cleanup renderAssistant() returns. So this module owns its own
 * teardown two ways:
 *   (a) a module-level `_activeTeardown` is torn down at the TOP of every
 *       renderAssistant() call, before anything else runs — re-opening the
 *       drawer for a new ctx (a second selection, another Ask AI click)
 *       always kills the prior subscription first.
 *   (b) a MutationObserver on the persistent `.wb-drawer` ancestor's
 *       `hidden` attribute detects an explicit close (Escape via
 *       workbench.js's mount-level keydown handler, or any future explicit
 *       closeDrawer() caller) and tears down immediately, rather than
 *       waiting on the run to finish naturally.
 * Both paths converge on the same teardown(), which unsubscribes (or, if the
 * dynamic import/subscribe hasn't landed yet, marks the run cancelled so it
 * never subscribes), resets the conversation's assistantRunAtom entry
 * (resetAssistantRun), and disconnects the observer.
 */
import { state, esc, escAttr } from "./state.js";
import { iconHtml } from "./icons.js";
import { assistantRunAtom, resetAssistantRun } from "./agent-store.js";
import { renderMarkdown } from "./agent-parts-md.js";
import { diffLines, extractFirstFencedBlock } from "./line-diff.js";
import { buildTablePrompt, buildAuditPrompt } from "./selection-prompt.js";

// The three canned questions for the Database view's "Ask about this table"
// chips-first mode (ctx.kind === "table"). Exported so a lightweight test can
// assert the chip list without spinning up DOM mocks; the actual
// chip-click -> send -> stream flow needs a real browser and is covered by
// the Database workbench E2E instead.
export const TABLE_QUESTIONS = [
  "Explain this table",
  "Where is it used in scripts?",
  "Suggest example queries",
];

// The three canned questions for the Audit view's "Ask AI" chips-first mode
// (ctx.kind === "audit"), same rationale as TABLE_QUESTIONS above.
export const AUDIT_QUESTIONS = [
  "Why was this changed?",
  "Is it safe to undo?",
  "What depends on this?",
];

// The currently-live render's teardown, if any. Singleton because only one
// assistant-drawer instance is ever open at a time (one `.wb-drawer` per
// workbench, one workbench visible at a time) — see the header comment.
let _activeTeardown = null;

// Cached so repeated drawer opens don't re-trigger the dynamic import --
// import() of an already-evaluated module resolves with the same cached
// module namespace either way, but there's no reason to re-ask for it.
let _mainModulePromise = null;
function _loadSendComposerText() {
  if (!_mainModulePromise) _mainModulePromise = import("./main.js");
  return _mainModulePromise.then((m) => m.sendComposerText);
}

// Bug fix (found while wiring the Improve diff card): a text part's content
// lives on `.md` (agent-parts.js's textPart() factory: `{type:"text", md,
// streaming}` -- the SAME field agent-activity.js's own
// `.filter(p => p.type === "text" && p.md).map(p => p.md)` reads), not
// `.text`. The prior version of this helper read `.text`, which no text
// part ever carries -- every accumulated answer silently rendered as an
// empty string regardless of ctx.kind. Never caught before because nothing
// exercised the streaming path end-to-end until this drawer's own E2E did.
function _partsText(parts) {
  return (parts || [])
    .filter((p) => p && p.type === "text")
    .map((p) => p.md || "")
    .join("");
}

// Diff-row op -> CSS class (workbench.css's .wb-diff colors from the
// --success/--error/--text-secondary design tokens).
const DIFF_OP_CLASS = { "+": "d-add", "-": "d-del", " ": "d-ctx" };

function _diffRowHtml(row) {
  const cls = DIFF_OP_CLASS[row.op] || "d-ctx";
  // A literal " " op glyph would collapse in HTML -- render it as an nbsp so
  // every row's op-prefix column still lines up under the +/- rows.
  const marker = row.op === " " ? " " : row.op;
  return `<div class="${cls}">${esc(marker)} ${esc(row.text)}</div>`;
}

/**
 * Render the Improve completion's diff card (or the "no code block found"
 * note) into `cardEl`, once the run has finished. Pure DOM population -- no
 * subscriptions, no async work of its own -- called synchronously from the
 * assistantRunAtom subscriber in renderAssistant() below, once per finished
 * run (the subscriber unsubscribes right after, so this never re-renders on
 * a later tick).
 * @param {{selectionText?:string, label?:string}} ctx
 * @param {HTMLElement} cardEl - the `.asst-improve-card` container
 * @param {string} answerText - the run's fully-accumulated streamed answer
 */
function _renderImproveCard(ctx, cardEl, answerText) {
  if (!cardEl) return;
  const block = extractFirstFencedBlock(answerText);
  if (!block) {
    cardEl.innerHTML = `<div class="asst-diff-empty">No code block detected.</div>`;
    return;
  }
  const rows = diffLines(ctx.selectionText || "", block.code).map(_diffRowHtml).join("");
  cardEl.innerHTML =
    `<div class="wb-diff">${rows}</div>` +
    `<div class="asst-diff-actions">` +
    `<button type="button" class="btn btn-xs asst-copy-code">Copy code</button>` +
    `<button type="button" class="btn btn-xs asst-apply-chat">Apply in chat</button>` +
    `</div>`;

  const copyBtn = cardEl.querySelector(".asst-copy-code");
  if (copyBtn) {
    copyBtn.addEventListener("click", async () => {
      try { await navigator.clipboard.writeText(block.code); }
      catch { /* clipboard permission/context unavailable -- silent no-op, same precedent as code-view.js's Copy button */ }
    });
  }

  const applyBtn = cardEl.querySelector(".asst-apply-chat");
  if (applyBtn) {
    applyBtn.addEventListener("click", () => {
      const prompt = "Apply this improvement to " + (ctx.label || "this fragment") + ":\n\n"
        + "```python\n" + block.code + "\n```";
      _loadSendComposerText().then((sendComposerText) => {
        sendComposerText(prompt);
        continueInChat();
      }).catch(() => { /* best-effort -- same silent-failure precedent as the drawer's own dynamic-import path */ });
    });
  }
}

/** Navigate to the Chat view. Dynamic import: shell-v2.js statically imports
 * many view modules (including, transitively, this one via code-view.js), so
 * a static back-import here would risk a cycle. */
export async function continueInChat() {
  const { showViewV2 } = await import("./shell-v2.js");
  showViewV2("chat");
}

/**
 * Render one assistant-drawer instance into `bodyEl`.
 * @param {{kind:'explain'|'improve'|'table'|'audit', label:string, prompt?:string, selectionText?:string, language?:string, table?:{name:string,isCustom?:boolean,columns?:string[]}, entry?:object}} ctx
 *   kind "explain"/"improve" carry `ctx.prompt` (auto-sent on open). kind
 *   "table" carries `ctx.table` instead and NO `ctx.prompt`; kind "audit"
 *   carries `ctx.entry` (the journal entry, including its already-loaded
 *   diff) instead and NO `ctx.prompt` -- both chips-first, see the header
 *   comment.
 * @param {HTMLElement} bodyEl - workbench.js's persistent `.wb-drawer-body` node
 * @returns {() => void} teardown — unsubscribes + resets the run. Also
 *   invoked automatically at the top of the NEXT renderAssistant() call and
 *   when the drawer is closed (see the header comment).
 */
export function renderAssistant(ctx, bodyEl) {
  // (a) Tear down whatever the drawer was previously showing before doing
  // anything else — re-opening for a new ctx must not leak the old run's
  // subscription or observer.
  if (_activeTeardown) {
    _activeTeardown();
    _activeTeardown = null;
  }
  if (!bodyEl) return () => {};

  // Mutable: for kind "explain"/"improve" this is also the send-time convId
  // (the auto-send below fires synchronously, same tick as open) -- but for
  // the chips-first kinds ("table", "audit") the prompt is sent later, on a
  // chip click, possibly after the user has switched conversations while the
  // drawer sat open unclicked.
  // `_runPrompt` re-reads state.activeSessionId at send time and reassigns
  // this variable so the subscription below and resetAssistantRun() in
  // teardown() always key off the conversation the run actually went into,
  // not the one active when the drawer was opened.
  let convId = state.activeSessionId;
  const isTable = ctx.kind === "table";
  const isAudit = ctx.kind === "audit";
  const isChips = isTable || isAudit;
  const chipQuestions = isAudit ? AUDIT_QUESTIONS : TABLE_QUESTIONS;

  bodyEl.innerHTML =
    `<div class="asst-head">${iconHtml("sparkles", { size: "sm" })}` +
    `<span class="asst-label">${esc(ctx.label || "")}</span>` +
    `<button type="button" class="btn btn-xs asst-continue"${isChips ? " disabled" : ""}>Continue in chat</button></div>` +
    (isChips
      ? `<div class="asst-chips">${chipQuestions
          .map((q) => `<button type="button" class="chip asst-chip" data-q="${escAttr(q)}">${esc(q)}</button>`)
          .join("")}</div>`
      : "") +
    `<div class="asst-status">${isChips ? "" : "Thinking…"}</div>` +
    `<div class="asst-answer" role="log" aria-live="polite"></div>` +
    (ctx.kind === "improve" ? `<div class="asst-improve-card"></div>` : "");

  const continueBtn = bodyEl.querySelector(".asst-continue");
  if (continueBtn) continueBtn.addEventListener("click", () => { continueInChat(); });

  const statusEl = bodyEl.querySelector(".asst-status");
  const answerEl = bodyEl.querySelector(".asst-answer");
  const improveCardEl = bodyEl.querySelector(".asst-improve-card");

  let cancelled = false;
  let unsubscribe = null;
  let observer = null;

  function teardown() {
    cancelled = true;
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    resetAssistantRun(convId);
    if (_activeTeardown === teardown) _activeTeardown = null;
  }

  // Shared send+subscribe+stream flow. Used both for the auto-send path
  // (explain/improve, invoked once immediately below with ctx.prompt) and
  // the chips-first chip-click path (table/audit, invoked once per chip
  // click, from the listener wired further down) -- neither path duplicates
  // the subscription/GC logic, both funnel through here.
  function _runPrompt(prompt) {
    // Re-read at send time, not open time -- sendComposerText() dispatches
    // into whatever state.activeSessionId is RIGHT NOW (main.js's
    // _dispatchTurn reads it at the same call), so the subscription below
    // must key off the same value or it watches the wrong (possibly no
    // longer active) conversation's atom entry. For explain/improve this
    // fires the same tick as the outer `convId` was captured, so it's a
    // no-op re-read; for a chips-first kind (a chip click, arbitrarily later)
    // it can differ if the user switched conversations while the drawer sat
    // open.
    const sendConvId = state.activeSessionId;
    _loadSendComposerText().then((sendComposerText) => {
      if (cancelled) return; // torn down before the module finished loading
      const sent = sendComposerText(prompt);
      if (!sent) {
        if (statusEl) statusEl.textContent = "Agent is busy. Wait for the current run to finish.";
        if (continueBtn) continueBtn.disabled = true;
        return; // nothing dispatched -- nothing to subscribe to; a prior
                // run's display (if any) is left exactly as it was
      }
      // sendComposerText() only returns true when nothing was already in
      // flight for this conversation, so any earlier run THIS drawer
      // instance was showing is guaranteed finished by now -- but its
      // subscription/answer are still stale from that finished run. Tear
      // down + reset before subscribing again so assistantRunAtom's
      // immediate-fire (store.js: "fires immediately with the current
      // value") can't flash the OLD answer for a beat before this run's
      // first event lands. (Defensive: `unsubscribe` should already be null
      // in the normal finished-run case -- this is the "same as reopening"
      // teardown discipline from the header comment, scoped to one
      // still-open drawer instance rather than the whole module.)
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      // Commit the send-time conversation as the one this drawer instance now
      // tracks -- the subscription just below AND teardown()'s own
      // resetAssistantRun() (whenever it eventually runs) both read the outer
      // `convId`, so both must agree with where the prompt actually went.
      convId = sendConvId;
      resetAssistantRun(convId);
      if (statusEl) statusEl.textContent = "Thinking…";
      if (answerEl) answerEl.innerHTML = "";
      if (improveCardEl) improveCardEl.innerHTML = "";
      if (continueBtn) continueBtn.disabled = false;

      unsubscribe = assistantRunAtom.subscribe((all) => {
        const entry = all[convId];
        if (!entry) return;
        const text = _partsText(entry.parts);
        if (statusEl) statusEl.textContent = entry.running ? "Thinking…" : "";
        if (answerEl) answerEl.innerHTML = renderMarkdown(text);
        if (!entry.running) {
          // Improve-only: once the answer is fully accumulated, extract its
          // first fenced code block and render the diff card (or the "no code
          // block" note) below the streamed answer -- see this module's own
          // header comment and _renderImproveCard()'s doc comment. The
          // chips-first kinds never render an improve card (no
          // `.asst-improve-card` container in their markup above, and
          // ctx.kind is "table"/"audit", not "improve").
          if (ctx.kind === "improve") _renderImproveCard(ctx, improveCardEl, text);
          if (unsubscribe) {
            // The run is done — stop listening for further ticks. The
            // MutationObserver below still owns resetAssistantRun() on actual
            // close, so the finished answer stays visible/re-readable until
            // the drawer closes or (chips-first kinds) another chip is clicked.
            unsubscribe();
            unsubscribe = null;
          }
        }
      });
    }).catch(() => {
      // The dynamic import (or sendComposerText itself, since it's chained in
      // the same .then) rejected. Surface it instead of leaving the drawer
      // stuck at "Thinking…" with an unhandled rejection.
      if (cancelled) return; // torn down before the failure surfaced
      if (statusEl) statusEl.textContent = "Could not reach the agent.";
    });
  }

  if (isChips) {
    bodyEl.querySelectorAll(".asst-chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        const question = btn.getAttribute("data-q") || btn.textContent || "";
        _runPrompt(isAudit ? buildAuditPrompt(question, ctx.entry || {}) : buildTablePrompt(question, ctx.table || {}));
      });
    });
  } else {
    _runPrompt(ctx.prompt);
  }

  const drawerEl = bodyEl.closest(".wb-drawer");
  if (drawerEl && typeof MutationObserver !== "undefined") {
    observer = new MutationObserver(() => {
      if (drawerEl.hasAttribute("hidden")) teardown();
    });
    observer.observe(drawerEl, { attributes: true, attributeFilter: ["hidden"] });
  }

  _activeTeardown = teardown;
  return teardown;
}

/**
 * Force-teardown the currently-live drawer instance, if any, without waiting
 * for a close event or the next renderAssistant() call. For callers that
 * blow away the drawer's DOM out from under it without going through
 * workbench.js's closeDrawer() (e.g. a full workbench re-mount on tenant
 * switch) — see code-view.js's resetCodeView().
 */
export function teardownAssistantDrawer() {
  if (_activeTeardown) {
    _activeTeardown();
    _activeTeardown = null;
  }
}
